import { useCallback, useEffect, useState } from "react";
import OnboardingGateDesktop from "./OnboardingGateDesktop";
import OnboardingGateMobile from "./OnboardingGateMobile";
import OnboardingRegisterPanel from "./OnboardingRegisterPanel";

const INITIAL_ACCEPTED = { 1: false, 2: false, 3: false, 4: false, 5: false };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}

/**
 * Wizard de bienvenida (5 pasos) y formulario de nickname + contraseña al final.
 * Desktop: carril horizontal con slide. Mobile: carril vertical y acordeones.
 *
 * @param {{ leadRequestId: string; onComplete?: (data?: { nickname: string }) => void }} props
 */
export default function OnboardingGate({ leadRequestId, onComplete }) {
  const [phase, setPhase] = useState("wizard");
  const [currentStep, setCurrentStep] = useState(1);
  const [acceptedSteps, setAcceptedStepsState] = useState(() => ({
    ...INITIAL_ACCEPTED,
  }));

  const setAccepted = useCallback((step, value) => {
    setAcceptedStepsState((prev) => ({ ...prev, [step]: value }));
  }, []);

  const canAdvance = acceptedSteps[currentStep];

  const nextStep = useCallback(() => {
    if (!acceptedSteps[currentStep]) return;
    setCurrentStep((s) => Math.min(5, s + 1));
  }, [acceptedSteps, currentStep]);

  const prevStep = useCallback(() => {
    setCurrentStep((s) => Math.max(1, s - 1));
  }, []);

  const finish = useCallback(() => {
    if (!acceptedSteps[5]) return;
    setPhase("register");
  }, [acceptedSteps]);

  const handleRegistered = useCallback(
    (data) => {
      onComplete?.(data);
    },
    [onComplete]
  );

  const isDesktop = useIsDesktop();

  if (phase === "register") {
    return (
      <OnboardingRegisterPanel
        leadRequestId={leadRequestId}
        onRegistered={handleRegistered}
        onBack={() => setPhase("wizard")}
      />
    );
  }

  const shared = {
    currentStep,
    acceptedSteps,
    setAccepted,
    nextStep,
    prevStep,
    finish,
    canAdvance,
  };

  return isDesktop ? (
    <OnboardingGateDesktop {...shared} />
  ) : (
    <OnboardingGateMobile {...shared} />
  );
}
